<script lang="ts" setup>
import { computed, ref } from 'vue';

import { useBrowserStore } from '@apps/main/new-desktop/core/stores';

import { AppLinkButton, AppFlicker, AppSvgIcon, AppTooltip } from '@libs/ui';
import type { IngredientId } from '@libs/trip-planner-interfaces';

import {
  BracketType,
  RelativeMoneyFormatter,
} from '../../../../../core/components/formatters';
import { FerryUpsellType } from '../../../models';
import type { FerryTicketPrices } from '../../../models';
import {
  useTripOverviewModalsStore,
  useTripOverviewPageStore,
  useUpsellStore,
} from '../../../stores';

import FerryProductCardTickets from './FerryProductCardTickets.vue';

interface FerryProductCardUpsellProps {
  ticket: FerryTicketPrices;
  vehicles: string[];
  ingredientId: IngredientId;
  token: string;
  printMode: boolean;
  allowVehicles: boolean;
  cabin?: boolean;
  transportationName?: string;
}

interface FerryProductCardUpsellEmits {
  (event: 'change-upsell', upsellType: FerryUpsellType): void;
}

const props = defineProps<FerryProductCardUpsellProps>();

const emit = defineEmits<FerryProductCardUpsellEmits>();

const tripOverviewPageStore = useTripOverviewPageStore();
const upsellStore = useUpsellStore();
const browserStore = useBrowserStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();

const hasTicketCategories = ref<boolean>(false);

const isLoading = computed<boolean>(() => {
  if (props.ingredientId) {
    return upsellStore.isLoadingDictionary.has(props.ingredientId);
  }

  return false;
});

function openChangeTicketCategoryModal(): void {
  if (hasTicketCategories.value) {
    tripOverviewModalsStore.openChangeTicketCategoryModal(
      props.token,
      props.ingredientId
    );
  }
}
</script>

<template>
  <div class="ferry-card-product-upsell">
    <div
      v-if="!browserStore.isSmallDevice && props.transportationName"
      class="upsell-item"
    >
      <div class="value">
        {{ props.transportationName }}
      </div>
    </div>
    <div class="upsell-item" @click.stop="openChangeTicketCategoryModal()">
      <div v-if="isLoading">
        <AppFlicker
          height="spacing-16"
          width="spacing-96"
          border-radius="border-radius-8"
        />
      </div>

      <div v-else class="value">
        <div class="description">
          <div class="ticket-type">{{ props.ticket.type }}</div>

          <div
            v-if="
              props.ticket.price &&
              props.ticket.basePrice &&
              props.ticket.price !== props.ticket.basePrice
            "
            class="price"
          >
            <RelativeMoneyFormatter
              :data="{
                price: props.ticket.price,
                basePrice: props.ticket.basePrice,
                color: 'color-secondary-40',
                font: 'font-label-s-light',
                bracketType: BracketType.Round,
              }"
            />
          </div>
        </div>
        <FerryProductCardTickets
          v-if="!tripOverviewPageStore.isLoading && !props.printMode"
          :token="props.token"
          :ingredient-id="props.ingredientId"
          @ticket-categories-loaded="hasTicketCategories = true"
        />
      </div>
    </div>
    <div
      v-if="vehicles.length || props.allowVehicles"
      class="upsell-item"
      @click.stop="emit('change-upsell', FerryUpsellType.Vehicle)"
    >
      <div class="value">
        <div v-if="vehicles.length" class="vehicles">
          {{ `${vehicles.length} ${$t('planner.vehicle')}` }}
        </div>
        <AppLinkButton
          v-if="props.allowVehicles && !props.printMode"
          id="change-vehicle-category-button"
          class="edit-vehicles"
          :text="
            vehicles.length
              ? $t('planner.edit')
              : $t('planner.transport.add_vehicle')
          "
          size="small"
          @click="emit('change-upsell', FerryUpsellType.Vehicle)"
        />
      </div>
    </div>
    <div v-if="props.cabin" class="upsell-item">
      <div class="value">
        <span>{{ $t('planner.cabin_seat_included') }}</span>
        <AppTooltip
          :position="browserStore.isSmallDevice ? 'bottom' : 'bottom-right'"
        >
          <template #trigger>
            <AppSvgIcon name="infoCircle" size="18" />
          </template>
          <template #default>
            {{ $t('planner.can_be_changed_in_later_step') }}
          </template>
        </AppTooltip>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.ferry-card-product-upsell {
  display: flex;
  justify-content: flex-start;

  @include for-small-devices-max {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
  }

  .upsell-item {
    display: flex;
    justify-content: flex-start;
    padding: 0 var(--spacing-16);
    border-right: solid 1px var(--color-overlay);
    @include for-small-devices-max {
      border-right: none;
      padding: 0;
    }

    .value {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      flex-direction: row;
      gap: var(--spacing-8);
      font: var(--font-label-s-light);
      color: var(--color-secondary-60);

      @include for-small-devices-max {
        justify-content: space-between;
      }

      .description {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        flex-direction: row;
        gap: var(--spacing-8);
      }

      .vehicles {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        white-space: wrap;

        @include for-small-devices-max {
          gap: var(--spacing-4);
          flex-direction: row;
          align-items: start;
        }
      }

      .edit-vehicles {
        color: var(--color-secondary-60);
      }
    }
  }
  .upsell-item:last-child {
    border-right: none;
    @include for-small-devices-max {
      grid-column: 1 / span 2;
      padding-top: var(--spacing-12);
    }
  }
}
</style>
